import { observable, action, computed, makeObservable } from "mobx";
import { AuthClient, isPermanentRefreshError } from "src/network/AuthClient";
import { NetworkConfig } from "src/network/NetworkConfig";
import { User, Role, ErpType } from "src/network/User";
import { RootStore } from "./RootStore";

export const AUTH_STORE_PERSIST_NAME = "@portal";

const TOKEN_REFRESH_LOCK_NAME = "wwportal-token-refresh";

type TokenRefreshLockManager = {
    request<T>(name: string, callback: () => Promise<T>): Promise<T>;
};

export class AuthStore {
    rootStore: RootStore;
    loaded = false;
    relogin = false;
    login = false;
    logout = false;
    user? = undefined as User | undefined;
    token? = undefined as string | undefined;
    currentlyUsedRole: string | undefined;
    refetchCustomerData: boolean = false;
    isCopyUser: boolean = false;

    refreshToken? = undefined as string | undefined;
    tokenExpirationTimestamp: number = 0;
    isLoginProcessRunning: boolean = false;
    loginCodeVerifier: string = "";
    entryPath: string = "";
    entryUri: string = "";
    loginRedirectUri: string = "";

    authClient: AuthClient;
    timer?: NodeJS.Timeout;
    visibilityCheckHandler?: () => void;
    tokenType = "Bearer";
    refreshTokenResponseType = "code";
    refreshRetryCount: number = 0;
    maxRefreshRetries: number = 3;
    refreshPromise?: Promise<boolean>;

    loginCodeChallengeMethod = "S256";
    scopes = "wwimmo-mobile-core-api openid profile offline_access";

    private setRefreshTimer(active: boolean, intervalMs: number) {
        if (this.timer) {
            clearTimeout(this.timer);
        }

        if (active) {
            this.timer = setTimeout(() => this.performTokenRefresh(), intervalMs);
        }
    }

    private async performTokenRefresh() {
        try {
            const success = await this.refreshTokens();
            if (success) {
                // Reset retry count on successful refresh
                this.refreshRetryCount = 0;
            } else {
                console.warn("Token refresh returned false");
                this.handleRefreshFailure();
            }
        } catch (error) {
            console.error(`Token refresh failed: ${error}`);
            this.handleRefreshFailure();
        }
    }

    public refreshTokens(): Promise<boolean> {
        if (!this.refreshPromise) {
            this.refreshPromise = this.withCrossTabRefreshLock(() => this.refreshWithLatestToken()).finally(() => {
                this.refreshPromise = undefined;
            });
        }

        return this.refreshPromise;
    }

    private withCrossTabRefreshLock<T>(callback: () => Promise<T>): Promise<T> {
        const lockManager =
            typeof navigator !== "undefined"
                ? (navigator as Navigator & { locks?: TokenRefreshLockManager }).locks
                : undefined;

        if (lockManager) {
            return lockManager.request(TOKEN_REFRESH_LOCK_NAME, callback);
        }

        return callback();
    }

    private async refreshWithLatestToken(): Promise<boolean> {
        const refreshToken = this.readPersistedRefreshToken() ?? this.refreshToken;

        try {
            return await this.authClient.refresh(refreshToken);
        } catch (error) {
            let finalError = error;

            const latestRefreshToken = this.readPersistedRefreshToken();
            if (isPermanentRefreshError(error) && latestRefreshToken && latestRefreshToken !== refreshToken) {
                try {
                    return await this.authClient.refresh(latestRefreshToken, true);
                } catch (retryError) {
                    finalError = retryError;
                }
            }

            if (isPermanentRefreshError(finalError)) {
                this.removeUser();
            }

            throw finalError;
        }
    }

    private readPersistedRefreshToken(): string | undefined {
        try {
            const persistedAuthStore = JSON.parse(window.localStorage.getItem(AUTH_STORE_PERSIST_NAME) ?? "{}");
            return typeof persistedAuthStore.refreshToken === "string" ? persistedAuthStore.refreshToken : undefined;
        } catch {
            return undefined;
        }
    }

    private handleRefreshFailure() {
        if (!this.refreshToken) {
            this.refreshRetryCount = 0;
            return;
        }

        this.refreshRetryCount++;

        if (this.refreshRetryCount <= this.maxRefreshRetries) {
            // Exponential backoff: 10s, 20s, 40s
            const retryDelayMs = Math.min(10000 * Math.pow(2, this.refreshRetryCount - 1), 60000);
            console.warn(
                `Token refresh retry ${this.refreshRetryCount}/${this.maxRefreshRetries} in ${retryDelayMs}ms`
            );

            this.timer = setTimeout(() => this.performTokenRefresh(), retryDelayMs);
        } else {
            console.error("Token refresh failed after max retries. User needs to re-authenticate.");
            // Don't schedule another refresh - let the error handler in Apollo catch it on next request
            this.refreshRetryCount = 0;
        }
    }

    private checkTokenExpiration() {
        if (!this.token || !this.refreshToken || !this.tokenExpirationTimestamp) {
            return;
        }

        const now = Date.now();
        const timeUntilExpiration = this.tokenExpirationTimestamp - now;

        // If token expires in less than 5 minutes, refresh it immediately
        if (timeUntilExpiration < 5 * 60 * 1000 && timeUntilExpiration > 0) {
            console.log("Token expiring soon, refreshing proactively");
            this.performTokenRefresh();
        } else if (timeUntilExpiration <= 0) {
            console.warn("Token already expired, refreshing immediately");
            this.performTokenRefresh();
        }
    }

    private setupVisibilityHandler() {
        if (typeof document === "undefined") {
            return;
        }

        // Clean up existing handler
        if (this.visibilityCheckHandler) {
            document.removeEventListener("visibilitychange", this.visibilityCheckHandler);
        }

        // Create new handler
        this.visibilityCheckHandler = () => {
            if (document.visibilityState === "visible") {
                this.checkTokenExpiration();
            }
        };

        document.addEventListener("visibilitychange", this.visibilityCheckHandler);
    }

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeObservable(this, {
            loaded: observable,
            relogin: observable,
            login: observable,
            logout: observable,
            user: observable,
            token: observable,
            currentlyUsedRole: observable,
            refetchCustomerData: observable,
            refreshToken: observable,
            tokenExpirationTimestamp: observable,
            isLoginProcessRunning: observable,
            loginCodeVerifier: observable,
            entryPath: observable,
            entryUri: observable,
            loginRedirectUri: observable,
            setLoaded: action,
            setRelogin: action,
            setLogin: action,
            setLogout: action,
            setRefreshToken: action,
            setToken: action,
            addUser: action,
            removeUser: action,
            changeRole: action,
            setErp: action,
            setMfaEnabled: action,
            setCurrentlyUsedRole: action,
            setIsLoginProcessRunning: action,
            setLoginCodeVerifier: action,
            setEntryPath: action,
            setEntryUri: action,
            setLoginRedirectUri: action,
            resetLoginVariables: action,
            setRefetchCustomerData: action,
            isLoggedIn: computed,
            portalRoleWithHighestPriority: computed
        });
        this.authClient = new AuthClient(NetworkConfig, this);
        this.setupVisibilityHandler();
    }

    setLoaded(loaded: boolean) {
        this.loaded = loaded;
    }

    setRelogin(relogin: boolean) {
        this.relogin = relogin;
    }

    setLogin(login: boolean) {
        this.login = login;
    }

    setLogout(logout: boolean) {
        this.logout = logout;
    }

    setRefreshToken(refresh_token: string | undefined, expires_in: number = 0) {
        this.refreshToken = refresh_token;
        const expiresInMs = expires_in * 0.9 * 1000;
        this.tokenExpirationTimestamp = expiresInMs > 0 ? Date.now() + expiresInMs : 0;
        this.setRefreshTimer(this.refreshToken !== undefined, expiresInMs);
    }

    setToken(token: string | undefined) {
        this.token = token;
    }

    addUser = (user: User, accessToken: string, refreshToken: string, expiresIn: number) => {
        this.token = accessToken;
        this.setRefreshToken(refreshToken, expiresIn);

        if (user.userid) {
            this.rootStore.userSettingsStore.init(user.userid);
        }

        // Fire-and-forget: loadAllEnums handles its own errors and sets
        // isLoaded on success. The explicit void + catch suppresses any
        // unhandled rejection warnings if the internal handling ever changes.
        void this.rootStore.enumerationsStore.loadAllEnums().catch(() => {
            /* swallowed — loadAllEnums already logs */
        });

        this.user = user;
    };

    removeUser = () => {
        this.token = undefined;
        this.setRefreshToken(undefined);
        this.user = undefined;
    };

    changeRole = (role: Role) => {
        if (this.user) {
            this.user.role = role;
        }
    };

    setErp = (newErp: ErpType) => {
        if (this.user) {
            this.user.erpType = newErp;
        }
    };

    setMfaEnabled = (mfaEnabled: boolean) => {
        if (this.user) {
            this.user.mfaEnabled = mfaEnabled;
        }
    };

    setCurrentlyUsedRole = (currentlyUsedRole: string) => {
        this.currentlyUsedRole = currentlyUsedRole;
    };

    setIsLoginProcessRunning = (isLoginProcessRunning: boolean) => {
        this.isLoginProcessRunning = isLoginProcessRunning;
    };

    setLoginCodeVerifier = (loginCodeVerifier: string) => {
        this.loginCodeVerifier = loginCodeVerifier;
    };

    setEntryPath = (entryPath: string) => {
        this.entryPath = entryPath;
    };

    setEntryUri = (entryUri: string) => {
        this.entryUri = entryUri;
    };

    setLoginRedirectUri = (loginRedirectUri: string) => {
        this.loginRedirectUri = loginRedirectUri;
    };

    resetLoginVariables = () => {
        this.isLoginProcessRunning = false;
        this.loginCodeVerifier = "";
        this.entryPath = "";
        this.entryUri = "";
        this.loginRedirectUri = "";
    };

    setRefetchCustomerData = (refetchCustomerData: boolean) => {
        this.refetchCustomerData = refetchCustomerData;
    };

    setIsCopyUser(isCopyUser: boolean) {
        this.isCopyUser = isCopyUser;
    }

    // Public method to check if token needs refresh (can be called from Apollo middleware)
    public shouldRefreshToken(): boolean {
        if (!this.token || !this.refreshToken || !this.tokenExpirationTimestamp) {
            return false;
        }

        const now = Date.now();
        const timeUntilExpiration = this.tokenExpirationTimestamp - now;

        // Return true if token expires in less than 5 minutes
        return timeUntilExpiration < 5 * 60 * 1000;
    }

    public async ensureValidToken(force: boolean = false): Promise<boolean> {
        if (!force && !this.shouldRefreshToken()) {
            return true; // Token is still valid
        }

        try {
            const success = await this.refreshTokens();
            if (success) {
                this.refreshRetryCount = 0;
            }
            return success;
        } catch (error) {
            console.error("Error ensuring valid token:", error);
            return false;
        }
    }

    public async refreshRejectedToken(rejectedToken: string | undefined): Promise<boolean> {
        if (rejectedToken && this.token && rejectedToken !== this.token) {
            return true;
        }

        return this.ensureValidToken(true);
    }

    get isLoggedIn() {
        return this.user !== undefined && this.token !== undefined;
    }

    get portalRoleWithHighestPriority() {
        let portalRoleWithHighestPriority: Role | undefined;

        if (this.user?.availablePortalRoles && this.user?.availablePortalRoles.length > 0) {
            portalRoleWithHighestPriority = this.user?.availablePortalRoles[0];
        }

        return portalRoleWithHighestPriority;
    }
}
